import axios from "axios";
import Constants from "./constant";
import moment from "moment";
import CryptoJS from "crypto-js";
import { isAppVersionOutdatedError, showAppUpdateModal } from "./utils/appUpdate";
// import { Capacitor } from "@capacitor/core";
// import { SSLCertificateChecker } from "@aalzehla/capacitor-ssl-pinning";

export const axiosClient = axios.create({
    baseURL: `${Constants.env.lambdaApiServer}`,
});

// // SSL Pinning configuration - SHA-256 fingerprints for each domain
// // Format: Colon-separated hex (as returned by the plugin)
// // The plugin normalizes fingerprints, but it's safer to use the format it returns
// const SSL_PINS = {
//     "40bxy7a7v3.execute-api.ap-southeast-1.amazonaws.com": "BE:7F:8A:67:BF:34:48:41:6E:5C:06:9F:9C:75:A5:BD:74:75:4E:26:E8:0E:ED:E0:00:AA:2E:A3:BB:F9:4A:40",
//     "qvas3cc2q0.execute-api.ap-southeast-1.amazonaws.com": "CE:67:87:15:A6:9D:F2:6B:C5:CA:D3:6D:DA:FF:8B:7D:AA:70:24:60:BD:A9:0E:1F:56:74:5F:EB:07:72:2B:5B",
//     "y6idvn45ge.execute-api.us-east-1.amazonaws.com": "47DEQpj8HBSa+/TImW+5JCeuQeRkm5NMpJWZG3hSuFU=",
//     "parentapp.s3.ap-southeast-1.amazonaws.com": "RNc9o5Ty/ZI17AM/gdJg9qM8h2E65oFtMfcT1Fw314U=",
//     "fp-portal-uploads.s3.ap-southeast-1.amazonaws.com": "RNc9o5Ty/ZI17AM/gdJg9qM8h2E65oFtMfcT1Fw314U=",
// };
//
// // SSL Pinning interceptor for native platforms
// if (Capacitor.isNativePlatform()) {
//     axiosClient.interceptors.request.use(
//         async (config) => {
//             // Build full URL
//             const url = config.url || "";
//             const baseURL = config.baseURL || "";
//             const fullUrl = url.startsWith("http")
//                 ? url
//                 : `${baseURL}${url.startsWith("/") ? url : `/${url}`}`;
//
//             // Extract hostname
//             let hostname = null;
//             try {
//                 const urlObj = new URL(fullUrl);
//                 hostname = urlObj.hostname;
//             } catch (e) {
//                 // If URL parsing fails, try regex
//                 const match = fullUrl.match(/https?:\/\/([^/]+)/);
//                 hostname = match ? match[1] : null;
//             }
//
//             // Check if we have a pin for this hostname
//             if (hostname && SSL_PINS[hostname]) {
//                 const fingerprint = SSL_PINS[hostname];
//
//                 try {
//                     // Ensure URL is HTTPS
//                     const httpsUrl = fullUrl.startsWith("http://")
//                         ? fullUrl.replace("http://", "https://")
//                         : fullUrl;
//
//                     console.log(`[SSL Pinning] Checking ${hostname}`);
//
//                     // Call the plugin's checkCertificate method
//                     const result = await SSLCertificateChecker.checkCertificate({
//                         url: httpsUrl,
//                         fingerprint: fingerprint,
//                     });
//
//                     // Reject if fingerprint doesn't match
//                     if (!result.fingerprintMatched) {
//                         const errorMsg = `SSL Certificate pinning failed for ${hostname}`;
//                         console.error(`[SSL Pinning] ${errorMsg}`);
//                         return Promise.reject(new Error(errorMsg));
//                     }
//
//                     console.log(`[SSL Pinning] ✅ Certificate verified for ${hostname}`);
//                 } catch (error) {
//                     console.error(`[SSL Pinning] Error checking certificate:`, error);
//                     return Promise.reject(
//                         new Error(`SSL Certificate verification failed: ${error.message}`)
//                     );
//                 }
//             }
//
//             return config;
//         },
//         (error) => {
//             return Promise.reject(error);
//         }
//     );
// }
// Request blocking mechanism for childreload
let isReloadInProgress = false;
let pendingRequests = [];
const RELOAD_ENDPOINT = "/parentapp/childLoginReloadDetails";

// Callback registered by Reload.js so the response interceptor can trigger a reload
// when AUTH_EXPIRED is detected on a response that slipped past the request interceptor.
let _reloadTrigger = null;
export const setReloadTrigger = (fn) => { _reloadTrigger = fn; };

// Returns true when the stored last_reload_time is from a previous calendar day.
// Used both at module load (cold start) and inside the request interceptor (app resume).
const _isReloadDateStale = () => {
    try {
        const token = localStorage.getItem("auth_token");
        const reloadTime = localStorage.getItem("last_reload_time");
        if (!token || token === '""' || !reloadTime) return false;

        const dateString = JSON.parse(reloadTime);
        const givenDate = new Date(dateString);
        const today = new Date();
        givenDate.setHours(0, 0, 0, 0);
        today.setHours(0, 0, 0, 0);
        return givenDate.getTime() !== today.getTime();
    } catch {
        return false;
    }
};

// On cold start, pre-block before any component mounts and fires queries.
if (_isReloadDateStale()) {
    console.log("[RequestBlocking] Startup: reload needed — pre-blocking all requests");
    isReloadInProgress = true;
}

// Function to start blocking requests (called when reload starts)
export const startRequestBlocking = () => {
    console.log("[RequestBlocking] Starting request blocking for childreload");
    isReloadInProgress = true;
};

// Function to stop blocking requests (called when reload completes successfully)
export const stopRequestBlocking = () => {
    console.log("[RequestBlocking] Stopping request blocking, processing pending requests");
    isReloadInProgress = false;

    // Process all pending requests
    const requestsToProcess = [...pendingRequests];
    pendingRequests = [];

    requestsToProcess.forEach(({ resolve, config }) => {
        // The auth_token header is a date-based MD5 hash computed at call-site time.
        // Queued requests carry yesterday's hash, so we must refresh it before sending.
        if (config.headers) {
            const childId = config.headers.child_id;
            const parent = config.headers.parent;
            config.headers.auth_token = getAuthToken(childId, parent);
            console.log("[RequestBlocking] Refreshed auth_token for pending request:", config.url);
        }
        // Prevent the response interceptor from re-queuing the same request indefinitely
        // if the retry itself somehow still gets AUTH_EXPIRED (e.g. reload failed).
        config._isRetryAfterReload = true;
        console.log("[RequestBlocking] Processing pending request:", config.url);
        resolve(config);
    });
};

// Called when reload fails — rejects all queued requests so React Query can surface
// the error and schedule its own retry, rather than releasing them with a stale token
// that the server will immediately reject with AUTH_EXPIRED.
export const clearPendingRequests = (reason) => {
    console.log("[RequestBlocking] Clearing pending requests due to reload failure:", reason?.message ?? reason);
    isReloadInProgress = false;
    const requestsToReject = [...pendingRequests];
    pendingRequests = [];
    const err = reason instanceof Error ? reason : new Error(reason ?? 'Reload failed');
    requestsToReject.forEach(({ reject }) => {
        if (typeof reject === 'function') reject(err);
    });
};

// Request interceptor to block requests during reload
axiosClient.interceptors.request.use(
    (config) => {
        // Allow the reload request itself to proceed
        if (config.url?.includes(RELOAD_ENDPOINT)) {
            console.log("[RequestBlocking] Allowing reload request:", config.url);
            return config;
        }

        // If not already blocking, check the date on every request.
        // This catches the iOS app-resume race where React Query refetches
        // before the Capacitor appStateChange event reaches Reload.js.
        if (!isReloadInProgress && _isReloadDateStale()) {
            console.log("[RequestBlocking] Stale date detected in interceptor — pre-blocking:", config.url);
            isReloadInProgress = true;
        }

        // Block all other requests if reload is in progress
        if (isReloadInProgress) {
            console.log("[RequestBlocking] Blocking request during reload:", config.url);

            // Capture both resolve and reject so clearPendingRequests can reject them
            // if the reload itself fails (rather than releasing with a stale token).
            return new Promise((resolve, reject) => {
                pendingRequests.push({ resolve, reject, config });
            });
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor: if AUTH_EXPIRED reaches the response layer (a request slipped
// past the request interceptor), silently reload and retry instead of showing an error.
axiosClient.interceptors.response.use(
    (response) => {
        const data = response.data;
        if (
            data?.status === 'error' &&
            isAppVersionOutdatedError(data.error || data.message)
        ) {
            showAppUpdateModal(data.error || data.message);
            return response;
        }

        const isAuthExpired =
            data?.status === 'error' &&
            (data?.errorCode === 'AUTH_EXPIRED' ||
                (typeof data?.error === 'string' &&
                    data.error.toLowerCase().includes('expired auth token')));

        if (isAuthExpired && !response.config._isRetryAfterReload) {
            console.log("[RequestBlocking] AUTH_EXPIRED in response — queuing retry:", response.config.url);

            if (!isReloadInProgress) {
                console.log("[RequestBlocking] Triggering reload from response interceptor");
                isReloadInProgress = true;
                // force=true so handleReload bypasses the date check even if
                // last_reload_time happens to match today (edge case).
                _reloadTrigger?.(true);
            }

            // Hold this response as a pending retry; stopRequestBlocking will re-fire it
            // with a fresh token once reload completes.
            return new Promise((resolve, reject) => {
                pendingRequests.push({
                    resolve: (freshConfig) => {
                        axiosClient(freshConfig).then(resolve).catch(reject);
                    },
                    reject,
                    config: response.config,
                });
            });
        }

        return response;
    },
    (error) => Promise.reject(error)
);

let getCurrentDate = () => {
    return moment().utc().format("YYYY-MM-DD");
};
export const getAuthToken = (child_id = undefined, parent = undefined) => {
    const saltKey = "!foot@prints#education#";
    let token = getCurrentDate() + "_" + saltKey;
    console.log("Generating Token ...");

    if (child_id && parent) {
        token =
            child_id + "_" + getCurrentDate() + "_" + parent + "_" + saltKey;
    }
    // return crypto.createHash("md5").update(token).digest("hex");
    return CryptoJS.MD5(token).toString(CryptoJS.enc.Hex);
};

